<template>
	<div class="touch-card-data">
		<div v-if="!rows.length" class="text-center pa-6 text-disabled">{{ t('NoData') }}</div>

		<template v-else>
			<div v-if="quickColumns.length" class="touch-card-data__quick">
				<QuickColumn :columns="quickColumns" :model-value="visibleColumns" :default-visible-columns="defaultVisibleColumns" @update:model-value="emit('update:visibleColumns', $event)" />
			</div>

			<div v-if="groups.length" class="touch-card-data__quick">
				<QuickRowGroup :groups="groups" :model-value="groupColumns" :default-active-groups="defaultActiveGroups" @update:model-value="emit('update:groupColumns', $event)" />
			</div>

			<SearchSortByControl class="touch-card-data__sort" :items="sortOptions" v-model:sort-by="sortBy" v-model:sort-order="sortOrder" />

			<TouchCardGroup v-if="isGrouped" :nodes="groupedNodes" :columns="normalizedColumns" :title-fields="titleFields" :subtitle-fields="subtitleFields" :title-image-field="titleImageField" :title-tags-field="titleTagsField" :row-key="rowKey" :metric-layout="metricLayout" :chunk-size="chunkSize" :row-class-field="rowClassField" @cell-change="emit('cell-change', $event)" />
			<TouchCardList v-else :rows="sortedRows" :columns="normalizedColumns" :title-fields="titleFields" :subtitle-fields="subtitleFields" :title-image-field="titleImageField" :title-tags-field="titleTagsField" :row-key="rowKey" :metric-layout="metricLayout" :chunk-size="chunkSize" :row-class-field="rowClassField" @cell-change="emit('cell-change', $event)" />
		</template>
	</div>
</template>

<script setup>
import { ref, computed, watch } from 'vue';
import { useTranslator } from '@/composables/useTranslator';
import SearchSortByControl from '@/components/controls/SearchSortByControl.vue';
import QuickColumn from '@/components/agGrid/QuickColumn.vue';
import QuickRowGroup from '@/components/agGrid/QuickRowGroup.vue';
import TouchCardList from '@/components/agGrid/TouchCardList.vue';
import TouchCardGroup from '@/components/agGrid/TouchCardGroup.vue';

const t = useTranslator();

const props = defineProps({
	rowData: { type: Array, default: () => [] },
	columns: { type: Array, default: () => [] },
	titleField: { type: [String, Array], default: 'label' },
	subtitleFields: { type: Array, default: () => ['reference', 'ean'] },
	titleImageField: { type: String, default: null },
	titleTagsField: { type: String, default: null },
	rowKey: { type: String, default: 'id' },
	metricLayout: { type: String, default: 'grid' },
	sortStorageKey: { type: String, default: null },
	chunkSize: { type: Number, default: 40 },
	// Row grouping (QuickRowGroup hosted inside the card list, ag-Grid colIds as values).
	groups: { type: Array, default: () => [] },
	groupColumns: { type: Array, default: () => [] },
	defaultActiveGroups: { type: Array, default: () => [] },
	groupFields: { type: Object, default: () => ({}) },
	getGroupRowAgg: { type: Function, default: null },
	groupLeafLabel: { type: String, default: '' },
	leafCountField: { type: String, default: null },
	// Optional column picker (DataSharing hosts its own outside of this component).
	quickColumns: { type: Array, default: () => [] },
	visibleColumns: { type: Array, default: () => [] },
	defaultVisibleColumns: { type: Array, default: () => [] },
	// Optional group-subtitle builder. Null keeps the default "N <groupLeafLabel>" behaviour.
	groupCountLabel: { type: Function, default: null },
	// Row field holding an extra CSS class for the card (per-row accents).
	rowClassField: { type: String, default: null },
});

const emit = defineEmits(['update:groupColumns', 'update:visibleColumns', 'cell-change']);

const rows = computed(() => (Array.isArray(props.rowData) ? props.rowData : []));
const titleFields = computed(() => (Array.isArray(props.titleField) ? props.titleField : [props.titleField]).filter(Boolean));
const primaryTitleField = computed(() => titleFields.value[0] || 'label');

const normalizedColumns = computed(() =>
	(Array.isArray(props.columns) ? props.columns : []).map((column) => ({
		field: column.field,
		label: column.label || column.field,
		suffix: column.suffix || '',
		decimals: column.decimals ?? 0,
		evoField: column.evoField || null,
		evoFormatMetric: typeof column.evoFormatMetric === 'function' ? column.evoFormatMetric : null,
		fromField: column.fromField || null,
		sortLabel: column.sortLabel || null,
		type: column.type || null,
		objectiveField: column.objectiveField || null,
		colorField: column.colorField || null,
		classColorField: column.classColorField || null,
		isPercentage: Boolean(column.isPercentage),
		hideObjective: Boolean(column.hideObjective),
		needObjectiveLabel: Boolean(column.needObjectiveLabel),
		objectiveLabel: column.objectiveLabel || '',
		barColor: column.barColor || null,
		formatMetric: typeof column.formatMetric === 'function' ? column.formatMetric : null,
		deltaField: column.deltaField || null,
		deltaFormatMetric: typeof column.deltaFormatMetric === 'function' ? column.deltaFormatMetric : null,
		detail: Boolean(column.detail),
		// --- decorated / interactive metric types: 'chip' | 'icon' | 'switch' | 'input' | 'multiselect' ---
		// Row field holding an array of tag descriptors [{ text, icon, class }]. The caller pre-computes them,
		// so this component stays free of predicates and business rules.
		tagsField: column.tagsField || null,
		// Static control colour (v-switch); a per-row colour still goes through colorField.
		color: column.color || null,
		// Row field holding a boolean: THIS row's control is disabled. A whole-column lock is expressed by
		// setting the flag on every row, so there is only ever one mechanism.
		disabledField: column.disabledField || null,
		// Option list of a 'multiselect' column, in SelectControl's { id, name } shape.
		items: Array.isArray(column.items) ? column.items : null,
		min: Number.isFinite(column.min) ? column.min : null,
		max: Number.isFinite(column.max) ? column.max : null,
		step: Number.isFinite(column.step) ? column.step : null,
		// 'number' (default, today's behaviour) drives the SortBy comparator; 'text' switches to localeCompare.
		sortType: column.sortType === 'text' ? 'text' : 'number',
	}))
);

// #region SORT

function sortStorageField(suffix) {
	return props.sortStorageKey ? `${props.sortStorageKey}.${suffix}` : null;
}

const sortBy = ref((props.sortStorageKey && localStorage.getItem(sortStorageField('sortBy'))) || primaryTitleField.value);
const sortOrder = ref((props.sortStorageKey && localStorage.getItem(sortStorageField('sortOrder'))) || 'asc');

watch(sortBy, (value) => {
	if (props.sortStorageKey) localStorage.setItem(sortStorageField('sortBy'), value);
});
watch(sortOrder, (value) => {
	if (props.sortStorageKey) localStorage.setItem(sortStorageField('sortOrder'), value);
});

const sortOptions = computed(() => {
	const options = [{ id: primaryTitleField.value, name: t('LabelAZ') }];
	normalizedColumns.value.forEach((column) => {
		options.push({ id: column.field, name: column.sortLabel || (column.fromField ? `${column.label} ${t('P2')}` : column.label) });
		if (column.evoField) options.push({ id: column.evoField, name: `${t('Gap')} ${column.label}` });
	});
	return options;
});

// A persisted sort key can point at a column that no longer exists (different KPI, hidden measure).
watch(
	sortOptions,
	(options) => {
		if (!options.some((option) => option.id === sortBy.value)) {
			sortBy.value = primaryTitleField.value;
		}
	},
	{ immediate: true }
);

function getValue(row, field) {
	return row && field ? (row[field] ?? '') : '';
}

function getTitle(row) {
	const field = titleFields.value.find((candidate) => getValue(row, candidate) !== '');
	return field ? getValue(row, field) : '';
}

function sortRows(list) {
	const key = sortBy.value;
	const direction = sortOrder.value === 'asc' ? 1 : -1;
	// Text columns (strates, dates, labels) would all collapse to 0 through the numeric comparator.
	const column = normalizedColumns.value.find((candidate) => candidate.field === key);
	return [...list].sort((first, second) => {
		if (key === primaryTitleField.value) return String(getTitle(first)).localeCompare(String(getTitle(second))) * direction;
		if (column?.sortType === 'text') return String(getValue(first, key)).localeCompare(String(getValue(second, key))) * direction;
		return ((Number(first[key]) || 0) - (Number(second[key]) || 0)) * direction;
	});
}

const sortedRows = computed(() => sortRows(rows.value));

// #endregion

// #region ROW GROUPING

const groupLabelByColumn = computed(() => {
	const labels = {};
	(Array.isArray(props.groups) ? props.groups : []).forEach((group) => {
		if (typeof group === 'string') {
			labels[group] = group;
			return;
		}

		const groupLabel = group?.label || group?.name || '';
		const columnField = group?.columnField || group?.field;
		if (columnField) {
			labels[columnField] = groupLabel || columnField;
		}

		(Array.isArray(group?.levels) ? group.levels : []).forEach((level) => {
			const levelField = level?.columnField || level?.field;
			if (levelField) {
				labels[levelField] = [groupLabel, level?.label || level?.name].filter(Boolean).join(' ') || levelField;
			}
		});
	});
	return labels;
});

const groupChain = computed(() =>
	(Array.isArray(props.groupColumns) ? props.groupColumns : [])
		.map((colId) => {
			const mapped = props.groupFields?.[colId];
			const fields = (Array.isArray(mapped) ? mapped : [mapped]).filter(Boolean);
			return { colId, fields, label: groupLabelByColumn.value[colId] || '' };
		})
		.filter((level) => level.fields.length > 0)
);

const isGrouped = computed(() => groupChain.value.length > 0);

function getGroupValue(row, fields) {
	const field = fields.find((candidate) => getValue(row, candidate) !== '');
	return field ? String(getValue(row, field)) : '';
}

// Reuses the very same aggregation the ag-Grid gets, by shimming leaf rows as ag-Grid row nodes.
function aggregate(groupRows) {
	if (typeof props.getGroupRowAgg !== 'function') {
		return null;
	}

	return props.getGroupRowAgg({ nodes: groupRows.map((row) => ({ data: row })) }) || null;
}

function countLeaves(groupRows) {
	if (!props.leafCountField) {
		return groupRows.length;
	}

	return new Set(groupRows.map((row) => row?.[props.leafCountField]).filter((value) => value !== null && value !== undefined && value !== '')).size;
}

function buildCountLabel(children, groupRows, nextLevel) {
	if (typeof props.groupCountLabel === 'function') {
		return props.groupCountLabel({ rows: groupRows, children, leafCount: countLeaves(groupRows) }) || '';
	}

	const parts = [];
	if (children && nextLevel?.label) {
		parts.push(`${children.length} ${nextLevel.label}`);
	}

	// Grouping by delivery location makes every leaf group hold a single store: repeating "1 stores" on
	// each panel is noise, so the leaf count only shows up when it actually adds information.
	const leafCount = countLeaves(groupRows);
	if (props.groupLeafLabel && (children || leafCount > 1)) {
		parts.push(`${leafCount} ${props.groupLeafLabel}`);
	}

	return parts.join(' · ');
}

function sortNodes(nodes) {
	const key = sortBy.value;
	const direction = sortOrder.value === 'asc' ? 1 : -1;
	if (key === primaryTitleField.value) {
		return [...nodes].sort((first, second) => String(first.label).localeCompare(String(second.label)) * direction);
	}
	return [...nodes].sort((first, second) => ((Number(first.agg?.[key]) || 0) - (Number(second.agg?.[key]) || 0)) * direction);
}

function buildNodes(groupRows, depth, keyPrefix) {
	const level = groupChain.value[depth];
	const nextLevel = groupChain.value[depth + 1];
	const buckets = new Map();

	groupRows.forEach((row) => {
		const bucketKey = getGroupValue(row, level.fields);
		if (!buckets.has(bucketKey)) {
			buckets.set(bucketKey, []);
		}
		buckets.get(bucketKey).push(row);
	});

	return [...buckets.entries()].map(([bucketKey, bucketRows]) => {
		const nodeKey = `${keyPrefix}${level.colId}:${bucketKey}`;
		const children = nextLevel ? buildNodes(bucketRows, depth + 1, `${nodeKey}/`) : null;

		return {
			key: nodeKey,
			label: bucketKey || t('NoData'),
			agg: aggregate(bucketRows),
			countLabel: buildCountLabel(children, bucketRows, nextLevel),
			children,
			leaves: children ? null : bucketRows,
		};
	});
}

function sortTree(nodes) {
	return sortNodes(nodes).map((node) => ({
		...node,
		children: node.children ? sortTree(node.children) : null,
		leaves: node.leaves ? sortRows(node.leaves) : null,
	}));
}

// Grouping and aggregation are kept out of the sort pass: changing the sort order must not re-run
// getGroupRowAgg over every bucket.
const rawGroupNodes = computed(() => (isGrouped.value ? buildNodes(rows.value, 0, '') : []));
const groupedNodes = computed(() => sortTree(rawGroupNodes.value));

// #endregion
</script>

<style scoped lang="scss">
.touch-card-data {
	display: flex;
	flex-direction: column;
	gap: 8px;

	&__quick {
		min-width: 0;
	}
}
</style>
